import { useState, useEffect } from 'react';
import { fetchWithAuth } from './authHelpers';

export const useUser = () => {
    const [user, setUser] = useState(null);
    const [isGuest, setIsGuest] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [favorites, setFavorites] = useState(null);
    const [favoritesLoading, setFavoritesLoading] = useState(false);
    const [favoritesError, setFavoritesError] = useState(null);

    useEffect(() => {

        const fetchUser = async () => {
            try {
                const response = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/users/current`, 'GET');
                const data = await response.json();
                if (!response.ok) {
                    if (response.status === 401) {
                        setIsGuest(true);
                    }
                    throw new Error(data.message || 'Failed to fetch user data');
                }
                setUser(data);
            } catch (error) {
                setError(error.message);
            } finally {
                setLoading(false);
            }
        };

        fetchUser();
    }, []);

    const fetchFavorites = async (userNumber = null) => {
        setFavoritesLoading(true);
        setFavoritesError(null);
        try {
            const requestBody = userNumber ? { userNumber: parseInt(userNumber) } : {};
            const response = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/users/favorites`, 'POST', requestBody);
            if (!response.ok) {
                throw new Error('Failed to fetch favorites');
            }
            const data = await response.json();
            setFavorites(data);
        } catch (error) {
            setFavoritesError(error.message);
        } finally {
            setFavoritesLoading(false);
        }
    };

    const updateFavoriteTeam = async (teamId) => {
        try {
            const response = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/users/favorite-team`, 'PATCH', {
                teamId: teamId
            });
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to update favorite team');
            }
            const data = await response.json();

            // Update favorites state
            setFavorites(prev => ({
                ...prev,
                favoriteTeam: data.favoriteTeam
            }));

            return data;
        } catch (error) {
            setFavoritesError(error.message);
            throw error;
        }
    };

    const updateFavoritePlayer = async (playerId) => {
        try {
            const response = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/users/favorite-player`, 'PATCH', {
                playerId: playerId
            });

            if (!response.ok) {
                throw new Error('Failed to update favorite player');
            }

            const data = await response.json();

            // Update favorites state
            setFavorites(prev => ({
                ...prev,
                favoritePlayer: data.favoritePlayer
            }));

            return data;
        } catch (error) {
            setFavoritesError(error.message);
            throw error;
        }
    };

    const changeUsername = async (newUsername) => {
        try {
            const response = await fetchWithAuth(`${process.env.REACT_APP_BACKEND_URL}/users/change-username`, 'PATCH', {
                newUsername: newUsername
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to change username');
            }

            const data = await response.json();

            // Update user state with new username
            setUser(prev => ({
                ...prev,
                username: data.username
            }));

            return data;
        } catch (error) {
            setError(error.message);
            throw error;
        }
    };

    return {
        user,
        isGuest,
        loading,
        error,
        favorites,
        favoritesLoading,
        favoritesError,
        fetchFavorites,
        updateFavoriteTeam,
        updateFavoritePlayer,
        changeUsername
    };
};